import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { getArchive, submitContact } from '@/lib/api'
import { useSEO } from '@/hooks/useSEO'
import { SmartImage } from '../components/ui/SmartImage'

const NETWORK_NAME = 'The Archive Network'
const HUB_HOME = '/'
const REDIRECT_MS = 5000

// Archive-family aesthetic (ported from The Lineage Archive's design tokens —
// parchment/royal palette + Cormorant Garamond / Sora). Values are the archive's
// actual tokens; applied locally so the hub's global theme is untouched.
const FONT_DISPLAY = "'Cormorant Garamond', Georgia, serif"
const FONT_BODY = "'Sora', system-ui, sans-serif"
const PAGE_BG =
  'radial-gradient(circle at top, rgba(198,167,94,0.12), transparent 60%),' +
  'radial-gradient(circle at bottom right, rgba(198,167,94,0.05), transparent 55%),' +
  '#1A1410'

// Friendly text for the structured error codes the hub /api/contact returns.
const ERROR_MESSAGES: Record<string, string> = {
  rate_limited: 'Too many messages. Please wait a few minutes and try again.',
  invalid_email: 'Please enter a valid email address.',
  invalid_name: 'Please enter your name.',
  invalid_subject: 'Please enter a subject.',
  invalid_message: 'Please enter a message (up to 5000 characters).',
  archive_required: 'No archive was specified for this message.',
  unknown_archive: 'We could not identify that archive.',
  send_failed: 'Something went wrong sending your message. Please try again.',
  server_error: 'Something went wrong on our end. Please try again later.',
  invalid_request: 'Your message could not be processed. Please try again.',
}

// Shared input styling (parchment field, royal focus).
const FIELD_CLASS =
  'w-full px-4 py-3 rounded-md bg-[#14100D] border border-[#3A2F21] text-[#E8DCC6] ' +
  'placeholder-[#8B7355]/60 focus:outline-none focus:border-[#C6A75E] ' +
  'focus:ring-1 focus:ring-[#C6A75E]/40 transition-colors'
const LABEL_CLASS =
  'block text-xs uppercase tracking-[0.15em] text-[#D4C4A8]/80 mb-2'
const BUTTON_CLASS =
  'w-full py-3 rounded-md bg-[#C6A75E] text-[#1A1410] font-semibold tracking-wide ' +
  'hover:bg-[#B89B4F] hover:shadow-[0_0_40px_rgba(198,167,94,0.18)] ' +
  'transition-all disabled:opacity-50 disabled:cursor-not-allowed'

export default function ContactPage() {
  // SEO
  useSEO({
    title: 'Contact',
    description: 'Get in touch with The Archive Network — questions, corrections, or collaboration proposals. Send a message and we\'ll reply by email.',
    url: 'https://thearchivenetwork.com/contact',
  })

  const [searchParams] = useSearchParams()
  const slug = (searchParams.get('archive') || '').trim().toLowerCase()

  // Archive resolved from the registry (API), never from a user-supplied URL.
  const [archiveName, setArchiveName] = useState<string | null>(null)
  const [archiveUrl, setArchiveUrl] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [company, setCompany] = useState('') // honeypot

  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [error, setError] = useState('')

  // Resolve slug -> name + domain from the registry (same source the API uses).
  useEffect(() => {
    if (!slug) return
    let active = true
    getArchive(slug)
      .then((a) => {
        if (!active) return
        setArchiveName(a.name)
        setArchiveUrl(a.site_url)
      })
      .catch(() => {
        // Unknown slug -> neutral fallback (handled by render).
      })
    return () => {
      active = false
    }
  }, [slug])

  // Auto-redirect after a successful send.
  useEffect(() => {
    if (status !== 'sent') return
    const t = setTimeout(() => {
      if (archiveUrl) window.location.href = archiveUrl
      else window.location.href = HUB_HOME
    }, REDIRECT_MS)
    return () => clearTimeout(t)
    // target captured intentionally; archiveUrl is the only dynamic input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, archiveUrl])

  const displayName = archiveName || NETWORK_NAME

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('sending')
    setError('')

    const result = await submitContact({
      archive: slug,
      name: name.trim(),
      email: email.trim(),
      subject: subject.trim(),
      message: message.trim(),
      company, // honeypot — left blank by real users
    })

    if (result.ok) {
      setStatus('sent')
    } else {
      setStatus('error')
      setError(ERROR_MESSAGES[result.error || 'send_failed'] || ERROR_MESSAGES.send_failed)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-16"
      style={{ background: PAGE_BG, fontFamily: FONT_BODY }}
    >
      <div className="w-full max-w-lg">
        <div className="rounded-xl bg-[#211A14] border border-[#3A2F21] shadow-[0_0_60px_rgba(198,167,94,0.08)] p-8 sm:p-10">
          {/* Seal */}
          <div className="flex justify-center mb-6">
            <Link to="/" className="inline-flex items-center">
              <span className="block w-14 h-14 rounded-full overflow-hidden border border-[#C6A75E]/40 shadow-[0_0_20px_rgba(198,167,94,0.12)]">
                <SmartImage
                    src="/images/archive-network-seal.png"
                    alt={NETWORK_NAME}
                    sizes="54px"
                    width={54}
                    height={54}
                    loading="eager"
                    className="w-full h-full object-cover"
                  />
              </span>
            </Link>
          </div>

          {status === 'sent' ? (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#C6A75E]/10 border border-[#C6A75E]/30 flex items-center justify-center">
                <svg className="w-8 h-8 text-[#C6A75E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl text-[#F5EBD7] mb-4" style={{ fontFamily: FONT_DISPLAY }}>
                Message Sent
              </h1>
              <p className="text-[#D4C4A8]/80 mb-8 leading-relaxed">
                Thanks for reaching out
                {archiveName ? <> to <span className="text-[#F5EBD7]">{archiveName}</span></> : null}.
                We'll get back to you by email.
              </p>
              {archiveUrl ? (
                <a href={archiveUrl} className={`inline-block ${BUTTON_CLASS}`}>
                  Return to {archiveName}
                </a>
              ) : (
                <Link to="/" className={`inline-block ${BUTTON_CLASS}`}>
                  Return to {NETWORK_NAME}
                </Link>
              )}
              <p className="mt-5 text-xs text-[#8B7355]">Redirecting you back in a few seconds…</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="text-center mb-8">
                <p className="text-xs uppercase tracking-[0.3em] text-[#C6A75E] mb-3">Contact</p>
                <h1 className="text-3xl sm:text-4xl text-[#F5EBD7] leading-tight" style={{ fontFamily: FONT_DISPLAY }}>
                  {displayName}
                </h1>
                <p className="mt-3 text-sm text-[#D4C4A8]/70">
                  Send a message and we'll reply by email.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className={LABEL_CLASS}>Name</label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    maxLength={200}
                    className={FIELD_CLASS}
                  />
                </div>

                <div>
                  <label htmlFor="email" className={LABEL_CLASS}>Email Address</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    maxLength={320}
                    className={FIELD_CLASS}
                  />
                </div>

                <div>
                  <label htmlFor="subject" className={LABEL_CLASS}>Subject</label>
                  <input
                    id="subject"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                    maxLength={200}
                    className={FIELD_CLASS}
                  />
                </div>

                <div>
                  <label htmlFor="message" className={LABEL_CLASS}>Message</label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    maxLength={5000}
                    rows={6}
                    className={`${FIELD_CLASS} resize-y`}
                  />
                </div>

                {/* Honeypot — hidden from real users; bots that fill it are silently dropped. */}
                <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden="true">
                  <label htmlFor="company">Company</label>
                  <input
                    id="company"
                    type="text"
                    name="company"
                    tabIndex={-1}
                    autoComplete="off"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </div>

                {error && (
                  <p className="text-sm text-center text-[#E0A89B] bg-[#E0796A]/10 border border-[#E0796A]/20 rounded-md py-2 px-3">
                    {error}
                  </p>
                )}

                <button type="submit" disabled={status === 'sending'} className={BUTTON_CLASS}>
                  {status === 'sending' ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending…
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-xs text-[#8B7355]">
                Your message is sent securely through {NETWORK_NAME}.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
